import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import StandardTable from "../../../ui/components/StandardTable";
import SettingsTableRangePagination from "../../../components/utils/SettingsTableRangePagination";
import { InlineLoader } from "../../../components/loader/CharacterizationLoader";
import PdfExportProgressOverlay from "../../../components/loader/PdfExportProgressOverlay";
import ClientHistoryRow from "./ClientHistoryRow";
import { useClientCharacterizationHistory } from "./useClientCharacterizationHistory";
import { PeriodFilterModal } from "../../../ui/components/filters";
import StatusFilterModal from "../../characterization-2/characterizationList/StatusFilterModal";
import ZoneFilterModal from "./ZoneFilterModal";
import { exportCsvSummaryFromRows } from "../../characterization-2/core/utils/exportCsvSummaryFromRows";
import { resolveSuperContainerForExport } from "./clientUtils";

const RECORD_PER_PAGE = 10;

const getPeriodOptions = (t) => [
  { label: t("Last 6 months"), value: 6 },
  { label: t("Last 12 months"), value: 12 },
  { label: t("Last 3 months"), value: 3 },
];

const getStatusOptions = (t) => [
  { label: t("All"), value: "all" },
  { label: t("Image en traitement"), value: "ImageEnTraitement" },
  { label: t("A compléter"), value: "ACompleter" },
  { label: t("Correction en traitement"), value: "CorrectionEnTraitement" },
  { label: t("Validated"), value: "Validated" },
  { label: t("Error"), value: "Error" },
];

const ClientHistoryTab = ({ clientId }) => {
  const { t, i18n } = useTranslation();
  const [periodMonths, setPeriodMonths] = useState(6);
  const [periodDisplayLabel, setPeriodDisplayLabel] = useState(
    () => getPeriodOptions(t)[0].label,
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExportingSummary, setIsExportingSummary] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    percent: 0,
    label: "",
  });

  const { rows, isLoading, zoneOptions, containers, superContainers } =
    useClientCharacterizationHistory(clientId, {
      periodMonths,
      statusFilter,
      zoneFilter,
    });

  const statusLabel =
    getStatusOptions(t).find((o) => o.value === statusFilter)?.label ??
    t("All");

  const zoneLabel =
    zoneFilter === "all"
      ? t("All")
      : zoneOptions.find((z) => String(z.value) === String(zoneFilter))
          ?.label ?? t("All");

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * RECORD_PER_PAGE;
    return rows.slice(start, start + RECORD_PER_PAGE);
  }, [rows, currentPage]);

  const totalEntry = rows.length;

  const handleExportSummaryCsv = async () => {
    if (rows.length === 0) return;
    setIsExportingSummary(true);
    setExportProgress({ percent: 5, label: t("Loading...") });
    try {
      await exportCsvSummaryFromRows({
        rows: rows.map((row) => ({
          period: row,
          container: row.zoneSite,
          superContainer: resolveSuperContainerForExport(
            row.superContainerId,
            superContainers,
          ),
        })),
        i18n,
        t,
        onProgress: ({ percent, label }) =>
          setExportProgress({
            percent,
            label: label || t("Loading..."),
          }),
      });
    } catch (error) {
      console.error("Error exporting summary CSV:", error);
    } finally {
      setIsExportingSummary(false);
      setExportProgress({ percent: 0, label: "" });
    }
  };

  return (
    <div className="w-full">
      <Card className="table border-0 shadow-none bg-white rounded-lg">
        <StandardTable
          fluidColCount={4}
          fixedColWidth={100}
          columnAlignments={["left", "left", "left", "left", "right"]}
          topBar={
            <section className="table-standard-top-bar">
              <div className="table-standard-top-bar-inner">
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsPeriodModalOpen(true)}
                  >
                    <span>
                      {t("settingsClients.filterPeriod")} : {periodDisplayLabel}
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsStatusModalOpen(true)}
                  >
                    <span>
                      {t("settingsClients.filterStatus")} : {statusLabel}
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="search-input table-standard-filter-button"
                    onClick={() => setIsZoneModalOpen(true)}
                  >
                    <span>
                      {t("settingsClients.filterZones")} : {zoneLabel}
                    </span>
                    <i
                      className="fas fa-chevron-down table-standard-filter-icon"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className="char-button char-button-primary"
                    disabled={isExportingSummary || rows.length === 0}
                    onClick={handleExportSummaryCsv}
                  >
                    <i className="fas fa-file-csv mr-2" aria-hidden />
                    {t("Export CSV summary")}
                  </button>
                </div>
              </div>
            </section>
          }
          header={
            <div className="table-standard-header">
              <div className="table-standard-header-cell">
                {t("settingsClients.historyColumnReading")}
              </div>
              <div className="table-standard-header-cell">{t("Status")}</div>
              <div className="table-standard-header-cell">
                {t("settingsClients.historyColumnZone")}
              </div>
              <div className="table-standard-header-cell">
                {t("Last modification")}
              </div>
              <div className="table-standard-header-cell" />
            </div>
          }
          bottom={
            rows.length > 0 ? (
              <SettingsTableRangePagination
                totalEntry={totalEntry}
                recordPerPage={RECORD_PER_PAGE}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            ) : null
          }
        >
          {isLoading ? (
            <div className="table-standard-row">
              <div
                className="table-standard-row-cell justify-center min-h-[200px]"
                style={{ gridColumn: "1 / -1" }}
              >
                <InlineLoader height="200px" />
              </div>
            </div>
          ) : paginatedRows.length > 0 ? (
            paginatedRows.map((row, index) => (
              <ClientHistoryRow
                key={`${row.zoneSite?._id}-${row.start}-${index}`}
                row={row}
                containers={containers}
                superContainers={superContainers}
              />
            ))
          ) : (
            <div className="table-standard-row table-standard-row--empty">
              <div
                className="table-standard-row-cell"
                style={{ gridColumn: "1 / -1" }}
              >
                {t("settingsClients.historyEmpty")}
              </div>
            </div>
          )}
        </StandardTable>

        <PeriodFilterModal
          isOpen={isPeriodModalOpen}
          onClose={() => setIsPeriodModalOpen(false)}
          currentPeriodFilter={periodMonths}
          onApply={(months, label) => {
            setPeriodMonths(months === undefined ? 6 : months);
            setPeriodDisplayLabel(label || getPeriodOptions(t)[0].label);
            setCurrentPage(1);
          }}
        />
        <StatusFilterModal
          isOpen={isStatusModalOpen}
          onClose={() => setIsStatusModalOpen(false)}
          currentStatusFilter={statusFilter}
          onApply={(value) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
        />
        <ZoneFilterModal
          isOpen={isZoneModalOpen}
          onClose={() => setIsZoneModalOpen(false)}
          currentZoneFilter={zoneFilter}
          zoneOptions={zoneOptions}
          onApply={(value) => {
            setZoneFilter(value);
            setCurrentPage(1);
          }}
        />
      </Card>
      {isExportingSummary ? (
        <PdfExportProgressOverlay
          percent={exportProgress.percent}
          label={exportProgress.label}
        />
      ) : null}
    </div>
  );
};

export default ClientHistoryTab;
